/**
 * Users API services
 */

import apiClient from './client';
import type { User } from '@/types';

export interface TenantUser {
  id: string;
  tenantId: string;
  userId: string;
  roleId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    fullName: string;
  };
  roleName: string;
}

export interface InviteUserRequest {
  email: string;
  fullName: string;
  roleId: string;
}

const transformUser = (data: any): User => ({
  id: data.id,
  email: data.email,
  fullName: data.full_name,
  isSuperAdmin: data.is_super_admin,
  isActive: data.is_active,
  emailVerifiedAt: data.email_verified_at,
  lastLoginAt: data.last_login_at,
});

const transformTenantUser = (data: any): TenantUser => ({
  id: data.id,
  tenantId: data.tenant_id,
  userId: data.user_id,
  roleId: data.role_id,
  isActive: data.is_active,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
  user: {
    id: data.user_id,
    email: data.email,
    fullName: data.full_name,
  },
  roleName: data.role_name,
});

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
}

export const usersApi = {
  // Per-account UI preferences (font, theme, ...) stored server-side in ui_settings
  getMyPreferences: async (): Promise<Record<string, any>> => {
    const response = await apiClient.get('/v1/users/me/preferences');
    return response.data?.ui_settings || {};
  },

  updateMyPreferences: async (uiSettings: Record<string, any>): Promise<Record<string, any>> => {
    const response = await apiClient.put('/v1/users/me/preferences', { ui_settings: uiSettings });
    return response.data?.ui_settings || {};
  },

  // Role management
  listRoles: async (): Promise<Role[]> => {
    const response = await apiClient.get('/v1/users/roles');
    return response.data.map((r: any) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      permissions: r.permissions,
      isSystem: r.is_system,
    }));
  },

  // Tenant user management
  listTenantMembers: async (params?: { isActive?: boolean }): Promise<TenantUser[]> => {
    const queryParams: Record<string, string> = {};
    if (params?.isActive !== undefined) queryParams.is_active = String(params.isActive);
    const response = await apiClient.get('/v1/user-management/tenant-users', { params: queryParams });
    // Handle paginated response format
    const items = response.data.items || response.data;
    return items.map(transformTenantUser);
  },

  inviteUser: async (data: InviteUserRequest): Promise<TenantUser> => {
    const response = await apiClient.post('/v1/user-management/tenant-users/invite', {
      email: data.email,
      full_name: data.fullName,
      role_id: data.roleId,
    });
    return transformTenantUser(response.data);
  },

  updateTenantUser: async (id: string, data: { roleId?: string; isActive?: boolean }): Promise<TenantUser> => {
    const payload: any = {};
    if (data.roleId !== undefined) payload.role_id = data.roleId;
    if (data.isActive !== undefined) payload.is_active = data.isActive;

    const response = await apiClient.patch(`/v1/user-management/tenant-users/${id}`, payload);
    return transformTenantUser(response.data);
  },

  removeTenantUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/user-management/tenant-users/${id}`);
  },

  // Super admin user management
  listAllUsers: async (params?: { isActive?: boolean; search?: string }): Promise<User[]> => {
    const response = await apiClient.get('/v1/users', { params });
    return response.data.map(transformUser);
  },

  getUser: async (id: string): Promise<User> => {
    const response = await apiClient.get(`/v1/users/${id}`);
    return transformUser(response.data);
  },

  // Admin password reset - sends reset link to user's email
  sendPasswordResetLink: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/v1/auth/admin-reset-password', {
      email,
    });
    return response.data;
  },

  // Admin sets a new password directly (for super admins)
  setUserPassword: async (userId: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/v1/users/${userId}/set-password`, {
      new_password: newPassword,
    });
    return response.data;
  },
};
